import {
  IconButton,
  Text,
  DataTableColumnMenuButton,
  createDataTableColumnVisibility,
  type DataTableColumnVisibility,
} from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { deleteEmbodiment } from "@/api";
import { canModifyEmbodiment, type MePermissions, type UserRole } from "@/app";
import { hatcheryTextModes } from "@/app/theme";
import { ResourceBrowserLayout } from "@/components/browse";
import {
  EmbodimentComposerModal,
  EmbodimentDetailModal,
  resolveEmbodimentErrorMessage,
} from "@/components/embodiment";
import { FilterResultCount } from "@/components/filter";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { embodimentColumns } from "../embodimentColumns";
import { EmbodimentTableSource } from "../EmbodimentTableSource";
import type { EmbodimentTableRow } from "../EmbodimentTableRow";
import { formatEmbodimentTitle, formatEmptyEmbodimentMessage } from "../embodimentFormatters";
import type { EmbodimentDialogState } from "../EmbodimentPageTypes";
import { HatTextModal } from "@/components/modal";
import styles from "../EmbodimentPage.module.css";
import { tableStripError } from "@/components/table/tableStateMessages";

const defaultPageSize = 20;

type EmbodimentCatalogTabProps = {
  role: UserRole | null;
  // 서버가 산출한 삭제 권한. 받지 못했으면 null 이고 등급으로 폴백한다. 삭제 버튼 표시에 쓴다.
  permissions: MePermissions | null;
  // 페이지 헤더의 새 Embodiment 컴포저가 만든 변경 신호. 값이 증가하면 목록을 다시 조회한다.
  refreshSeq?: number;
  // 커밋된 검색어(URL 이 소유하고, 페이지 헤더의 필터 바가 쓴다). 탭은 소스에 전달만 한다.
  searchQuery?: string;
};

// Embodiment 카탈로그 탭. 목록, 상세(공용 모달), 편집(공용 컴포저), 삭제를 다룬다.
// 행을 클릭하면 읽기 전용 상세를 연다. admin 은 상세에서 편집이나 삭제로 진입한다.
// 생성 진입점(새 Embodiment)과 검색(필터 바)은 페이지 헤더가 소유한다. 결과 수와 새로고침은
// 목록 상단 띠가 표시한다(rules.md §17).
export function EmbodimentCatalogTab({
  role,
  permissions,
  refreshSeq = 0,
  searchQuery = "",
}: EmbodimentCatalogTabProps) {
  const canModify = canModifyEmbodiment(role);
  const [dialog, setDialog] = useState<EmbodimentDialogState>({ kind: "closed" });
  const [columnVisibility, setColumnVisibility] = useState<DataTableColumnVisibility>(() =>
    createDataTableColumnVisibility(embodimentColumns),
  );

  // 첫 검색어는 소스를 만들 때 전달한다. 그래서 initialize 한 번으로 로드가 끝난다. 아래 effect 의
  // 첫 실행은 no-op 이다.
  const [source] = useState(
    () =>
      new EmbodimentTableSource({
        pageSize: defaultPageSize,
        rowClickHandler: (row: EmbodimentTableRow) =>
          setDialog({ kind: "detail", embodiment: row }),
        resolveErrorMessage: resolveEmbodimentErrorMessage,
        initialSearchQuery: searchQuery,
      }),
  );

  useEffect(() => {
    void source.initialize();
    return () => {
      source.dispose();
    };
  }, [source]);

  useEffect(() => {
    void source.setSearchQuery(searchQuery);
  }, [searchQuery, source]);

  // 헤더 컴포저가 만든 변경을 반영한다. 신호가 증가했을 때만 다시 조회한다. 그래서 마운트 직후의
  // 초기 로드와 겹치지 않는다.
  const lastRefreshSeqRef = useRef(refreshSeq);
  useEffect(() => {
    if (lastRefreshSeqRef.current === refreshSeq) {
      return;
    }
    lastRefreshSeqRef.current = refreshSeq;
    void source.refresh();
  }, [refreshSeq, source]);

  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const { loadState, page, rows, size, total } = snapshot;
  const isLoading = loadState === "loading";
  const firstItem = total === 0 ? 0 : (page - 1) * size + 1;
  const lastItem = Math.min(page * size, total);
  const emptyMessage = formatEmptyEmbodimentMessage({
    hasSearchQuery: searchQuery !== "",
    searchQuery,
  });

  function closeDialog() {
    setDialog({ kind: "closed" });
  }

  async function handleDeleteConfirm() {
    if (dialog.kind !== "delete" || dialog.submitting) {
      return;
    }
    const target = dialog.embodiment;
    setDialog({ ...dialog, submitting: true, errorMessage: null });
    try {
      await deleteEmbodiment(target.uuid, { role });
      source.removeRow(target.uuid);
      setDialog({ kind: "closed" });
      await source.refresh();
    } catch (error) {
      setDialog({
        kind: "delete",
        embodiment: target,
        submitting: false,
        errorMessage: resolveEmbodimentErrorMessage(error),
      });
    }
  }

  function handleComposerApply() {
    // 컴포저가 생성·수정 결과를 직접 저장한다. 그래서 목록만 다시 받는다.
    setDialog({ kind: "closed" });
    void source.refresh();
  }

  return (
    <>
      <ResourceBrowserLayout
        ariaLabel="Embodiment 목록"
        summaryCount={total}
        summaryLabel="embodiments"
        summaryInFilter
        notice={canModify ? null : "Embodiment 생성, 수정, 삭제는 admin 권한에서만 가능합니다."}
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
        footer={
          <div className={styles.tableFooter} aria-label="Embodiment 테이블 컨트롤">
            {rows.length > 0 ? (
              <Text variant={hatcheryTextModes["body-normal"]} className={styles.range}>
                총 {total}개 중 {firstItem}-{lastItem}
              </Text>
            ) : (
              <span aria-hidden="true" />
            )}
            <DataTableColumnMenuButton
              columns={embodimentColumns}
              columnVisibility={columnVisibility}
              menuPlacement="top"
              onColumnVisibilityChange={setColumnVisibility}
            />
          </div>
        }
      >
        {/* 목록 상단 띠. 결과 수(idle)와 새로고침을 표시한다. 행 선택이 없는 목록이라 액션은 없다. */}
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            source={source}
            idleContent={<FilterResultCount count={total} unitLabel="embodiments" />}
            rightContent={
              <IconButton
                size="sm"
                text={isLoading ? "로딩 중…" : "새로고침"}
                variant="surface"
                disabled={isLoading}
                onClick={() => void source.refresh()}
              />
            }
            ariaLabel="Embodiment 목록 도구"
          />
          <HatDataTable
            source={source}
            columns={embodimentColumns}
            columnVisibility={columnVisibility}
            emptyMessage={emptyMessage}
            loadingMessage="Embodiment 목록을 불러오고 있습니다."
            ariaLabel="Embodiment 목록"
            scrollable
            minWidth="900px"
          />
        </div>
      </ResourceBrowserLayout>

      {dialog.kind === "detail" ? (
        <EmbodimentDetailModal
          open
          embodiment={dialog.embodiment}
          role={role}
          permissions={permissions}
          onClose={closeDialog}
          onEdit={() => setDialog({ kind: "composerEdit", embodimentUuid: dialog.embodiment.uuid })}
          onDelete={() =>
            setDialog({
              kind: "delete",
              embodiment: dialog.embodiment,
              errorMessage: null,
              submitting: false,
            })
          }
        />
      ) : null}

      {dialog.kind === "composerEdit" ? (
        <EmbodimentComposerModal
          open
          role={role}
          source={{ kind: "edit", embodimentUuid: dialog.embodimentUuid }}
          currentTip={null}
          onClose={closeDialog}
          onApply={handleComposerApply}
        />
      ) : null}

      {dialog.kind === "delete" ? (
        <HatTextModal
          open
          eyebrow={formatEmbodimentTitle(dialog.embodiment)}
          label="Embodiment를 삭제합니다."
          caption="이 Embodiment를 참조하는 Dataset이 있으면 서버가 삭제를 거부할 수 있습니다."
          error
          errorMessage={dialog.errorMessage ?? undefined}
          busy={dialog.submitting}
          okText="삭제"
          onOkPress={() => void handleDeleteConfirm()}
          onCancelPress={closeDialog}
        />
      ) : null}
    </>
  );
}
